import { PropsWithChildren, useCallback } from 'react';
import { createContext, useContext } from 'react';
import type { AvailableThemes as AvailableThemesType } from '@/_constants/AvailableThemes.ts';
import AvailableThemes from '@/_constants/AvailableThemes.ts';
import { Theme } from 'react-daisyui';
import { useLocalStorage } from '@/hooks/useLocalStorage.ts';

interface ThemeContextProps {
  activeTheme: AvailableThemesType;
  updateTheme: (theme: AvailableThemesType) => void;
}

const ThemeContext = createContext<ThemeContextProps>(undefined!);

export const ThemeProvider = ({ children }: PropsWithChildren) => {
  const [activeTheme, setActiveTheme] = useLocalStorage<AvailableThemesType>('activeTheme', AvailableThemes.LIGHT);

  const updateTheme = useCallback(
    (theme: AvailableThemesType) => {
      setActiveTheme(theme);
    },
    [setActiveTheme]
  );

  return (
    <ThemeContext.Provider
      value={{
        activeTheme,
        updateTheme,
      }}
    >
      <Theme dataTheme={activeTheme}>{children}</Theme>
    </ThemeContext.Provider>
  );
};

export function useThemeContext(): ThemeContextProps {
  const context = useContext(ThemeContext);

  if (typeof context === 'undefined') {
    throw new Error('useThemeContext should be used within the ThemeContext provider!');
  }

  return context;
}
